/**
 * @file apps/web/src/pages/ApprovalsInboxPage.tsx
 * @description Personal approvals inbox — shows all pending approval steps
 * assigned to the logged-in user across all projects.
 * Approve or reject directly from the inbox without navigating away.
 */

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, Inbox, AlertCircle, ArrowRight } from 'lucide-react';
import { useAuth } from '../lib/auth.js';
import { approvalsApi } from '../lib/api.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface InboxItem {
  step_id:             string;
  step_order:          number;
  activated_at:        string;
  is_required:         boolean;
  approval_id:         string;
  approval_title:      string;
  entity_type:         string;
  entity_id:           string;
  workflow_type:       string;
  project_id:          string;
  approval_created_at: string;
  prev_approver_name?: string | null;
  prev_decided_at?:    string | null;
}

// ---------------------------------------------------------------------------
// Inbox item card
// ---------------------------------------------------------------------------

function InboxCard({
  item,
  onDecide,
  deciding,
}: {
  item:     InboxItem;
  onDecide: (approvalId: string, stepId: string, decision: 'approved' | 'rejected') => Promise<void>;
  deciding: string | null;
}) {
  const waitingSince = Math.floor(
    (Date.now() - new Date(item.activated_at).getTime()) / (1000 * 60 * 60),
  );
  const waitLabel = waitingSince < 1
    ? 'just now'
    : waitingSince < 24
    ? `${waitingSince}h ago`
    : `${Math.floor(waitingSince / 24)}d ago`;

  const entityLabel = item.entity_type.replace(/_/g, ' ');

  const isDeciding = deciding === item.step_id;

  return (
    <div className="card p-4 space-y-3 border-l-4 border-blue">
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-mono font-semibold text-blue uppercase tracking-wide mb-0.5">
            Pending Your Action
          </p>
          <h3 className="text-sm font-semibold text-ink truncate">
            {item.approval_title}
          </h3>
          <p className="text-xs text-muted mt-0.5">
            {entityLabel} · Step {item.step_order} of{' '}
            <span className="font-medium">{item.workflow_type}</span> workflow
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0 text-xs font-mono text-muted">
          <Clock size={11} />
          {waitLabel}
        </div>
      </div>

      {/* Context: who approved before */}
      {item.prev_approver_name && (
        <div className="flex items-center gap-1.5 text-xs text-muted bg-sky/20 rounded px-2 py-1">
          <CheckCircle size={11} className="text-success" />
          Approved by <span className="font-medium text-ink">{item.prev_approver_name}</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={() => onDecide(item.approval_id, item.step_id, 'approved')}
          disabled={isDeciding}
          className="btn-primary text-xs py-1.5 px-4 font-semibold"
        >
          <CheckCircle size={13} />
          {isDeciding ? 'Saving…' : 'Approve'}
        </button>
        <button
          onClick={() => onDecide(item.approval_id, item.step_id, 'rejected')}
          disabled={isDeciding}
          className="btn-danger text-xs py-1.5 px-4 font-semibold"
        >
          <XCircle size={13} />
          Reject
        </button>
        <Link
          to={`/projects/${item.project_id}/approvals`}
          className="btn-ghost text-xs text-muted ml-auto"
        >
          View full workflow <ArrowRight size={11} />
        </Link>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ApprovalsInboxPage
// ---------------------------------------------------------------------------

export function ApprovalsInboxPage() {
  const { devHeaders } = useAuth();
  const [items, setItems]     = useState<InboxItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [deciding, setDeciding] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await approvalsApi.inbox(devHeaders) as InboxItem[];
      setItems(data);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load inbox');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleDecide = async (
    approvalId: string,
    stepId: string,
    decision: 'approved' | 'rejected',
  ) => {
    setDeciding(stepId);
    try {
      await approvalsApi.decide(approvalId, stepId, { decision }, devHeaders);
      setItems((prev) => prev.filter((i) => i.step_id !== stepId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to record decision');
    } finally {
      setDeciding(null);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-semibold text-ink">Approvals Inbox</h2>
          {!loading && (
            <p className="text-xs font-mono text-muted mt-0.5">
              {items.length === 0
                ? 'No pending actions'
                : `${items.length} item${items.length !== 1 ? 's' : ''} awaiting your decision`}
            </p>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm">
          <AlertCircle size={14} className="shrink-0" /> {error}
        </div>
      )}

      {loading ? (
        <div className="space-y-3 animate-pulse">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card h-28 bg-gray/60" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="empty-state py-20">
          <Inbox size={36} className="text-muted/30 mb-3 mx-auto" />
          <p className="text-sm font-medium text-muted">You're all caught up</p>
          <p className="text-xs text-muted/70 mt-1">No pending approvals require your action</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <InboxCard
              key={item.step_id}
              item={item}
              onDecide={handleDecide}
              deciding={deciding}
            />
          ))}
        </div>
      )}
    </div>
  );
}
